import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationTone, ProjectCover, ProjectRuleKind } from '@mailpilot/shared';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { useToast } from '../components/ui/Toast';

export function useProjects(enabled = true) {
  return useQuery({ queryKey: qk.projects, queryFn: () => api('GET /api/projects'), enabled, staleTime: 15_000 });
}

export function useProject(id: string | undefined) {
  return useQuery({ queryKey: qk.project(id ?? 'none'), queryFn: () => api('GET /api/projects/:id', { params: { id: id! } }), enabled: !!id });
}

export function useProjectThreads(id: string | undefined, filter?: 'unread') {
  return useInfiniteQuery({
    queryKey: qk.projectThreads(id ?? 'none', filter),
    queryFn: ({ pageParam }) => api('GET /api/projects/:id/threads', { params: { id: id! }, query: { cursor: pageParam ?? undefined, filter } }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!id,
  });
}

/** Every project mutation refreshes the project, the list and its conversations. */
function useProjectMutation<V, R>(fn: (v: V) => Promise<R>, opts: { success?: string } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.projects });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: qk.projectThreadsRoot });
      void qc.invalidateQueries({ queryKey: qk.threadsRoot });
      void qc.invalidateQueries({ queryKey: ['thread'] });
      if (opts.success) toast.success(opts.success);
    },
    onError: (e) => toast.error(e),
  });
}

export const useCreateProject = () =>
  useProjectMutation((b: { name: string; description?: string; cover?: ProjectCover; rules?: { kind: ProjectRuleKind; value: string }[]; memberIds?: string[] }) => api('POST /api/projects', { body: b }));
export const useUpdateProject = (id: string) => useProjectMutation((b: { name?: string; description?: string; cover?: ProjectCover }) => api('PATCH /api/projects/:id', { params: { id }, body: b }), { success: 'Project saved.' });
export const useDeleteProject = (id: string) => useProjectMutation(() => api('DELETE /api/projects/:id', { params: { id } }), { success: 'Project deleted.' });
export const useSetProjectRules = (id: string) => useProjectMutation((rules: { kind: ProjectRuleKind; value: string }[]) => api('PUT /api/projects/:id/rules', { params: { id }, body: { rules } }));
export const useSetProjectMembers = (id: string) =>
  useProjectMutation((members: { userId: string; role: 'manager' | 'member' }[]) => api('PUT /api/projects/:id/members', { params: { id }, body: { members } }), { success: 'Members updated.' });
export const useProjectPrefs = (id: string) => useProjectMutation((b: { notify?: boolean; tone?: NotificationTone }) => api('PATCH /api/projects/:id/me', { params: { id }, body: b }));
export const useAddToProject = () =>
  useProjectMutation((v: { projectId: string; threadId: string; alwaysForSender?: boolean }) => api('POST /api/projects/:id/threads', { params: { id: v.projectId }, body: { threadId: v.threadId, alwaysForSender: v.alwaysForSender } }), { success: 'Added to the project.' });
export const useRemoveFromProject = (id: string) =>
  useProjectMutation((threadId: string) => api('DELETE /api/projects/:id/threads/:threadId', { params: { id, threadId } }), { success: 'Removed from the project.' });
export const useDismissProjectSuggestion = () => useProjectMutation((threadId: string) => api('POST /api/threads/:id/project-suggestion/dismiss', { params: { id: threadId } }));

export function useProjectFiles(id: string | undefined) {
  return useQuery({ queryKey: ['project-files', id ?? 'none'], queryFn: () => api('GET /api/projects/:id/files', { params: { id: id! } }), enabled: !!id, staleTime: 30_000 });
}
export function useProjectNotes(id: string | undefined) {
  return useQuery({ queryKey: ['project-notes', id ?? 'none'], queryFn: () => api('GET /api/projects/:id/notes', { params: { id: id! } }), enabled: !!id });
}
export function useAddProjectNote(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (body: string) => api('POST /api/projects/:id/notes', { params: { id }, body: { body } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['project-notes', id] }),
    onError: (e) => toast.error(e),
  });
}
export function useDeleteProjectNote(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (noteId: string) => api('DELETE /api/projects/:id/notes/:noteId', { params: { id, noteId } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['project-notes', id] }),
    onError: (e) => toast.error(e),
  });
}

export async function uploadProjectCover(id: string, file: File): Promise<ProjectCover> {
  const fd = new FormData();
  fd.append('file', file);
  return (await api('POST /api/projects/:id/cover', { params: { id }, body: fd as never })).cover;
}
